import { AlertCircle, Loader2, RotateCw } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

import { FormattedDateTime } from '@/components/FormattedDateTime';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { JsonEditor } from '@/components/ui/json-editor';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { SimpleTooltipV2 } from '@/components/ui/tooltip';
import type { WebhookPermissions } from '@/hooks/useShouldShowWebhooksPage';

import { useRetryWebhookDelivery, useWebhookDelivery } from '../hooks/useWebhookDeliveries';
import {
  deliveryRetryAction,
  deliveryRetryActionLabel,
  EMPTY_VALUE,
  eventTypeLabel,
  LIVE_POLL_INTERVAL_MS,
  manualActionActor,
  manualActionLabel,
  prettyEventJson,
  webhookErrorMessage,
} from '../utils';
import { AttemptTimeline } from './AttemptTimeline';
import { DeliveryStatusBadge } from './StatusBadges';

interface DeliveryDrawerProps {
  organizationId: string;
  deliveryId: string;
  permissions: WebhookPermissions;
  /** False when the delivery's endpoint is paused or disabled (retry is refused). */
  endpointActive: boolean;
  /** Live mode: keep re-reading the delivery so new attempts show up. */
  live?: boolean;
  onClose: () => void;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm break-all">{children}</dd>
    </div>
  );
}

function CopyableId({ value, label }: { value: string | undefined; label: string }) {
  if (!value) return <>{EMPTY_VALUE}</>;
  return (
    <span className="inline-flex items-center gap-1 font-mono text-xs">
      {value}
      <CopyButton value={value} label={label} />
    </span>
  );
}

/**
 * One delivery in full: status, next attempt and retries left, the attempt
 * timeline, the exact event JSON that was signed and sent (ids only, no PHI),
 * and the manual-action log. Retry now / Resend per status.
 */
export function DeliveryDrawer({
  organizationId,
  deliveryId,
  permissions,
  endpointActive,
  live = false,
  onClose,
}: DeliveryDrawerProps) {
  const query = useWebhookDelivery(
    organizationId,
    deliveryId,
    permissions.canGetDelivery,
    live ? LIVE_POLL_INTERVAL_MS : false,
  );
  const retry = useRetryWebhookDelivery(organizationId);
  const detail = query.data;
  const delivery = detail?.delivery;
  const retryAction = deliveryRetryAction(delivery?.status);

  const handleRetry = () => {
    if (!retryAction) return;
    retry.mutate(deliveryId, {
      onSuccess: () =>
        toast.success(retryAction === 'retry' ? 'Retry started' : 'Event resent', {
          description:
            'Same event id, with the endpoint’s current settings and a fresh set of retries.',
        }),
      onError: err => toast.error(webhookErrorMessage(err, 'Could not retry this delivery')),
    });
  };

  const actions = (detail?.manual_actions ?? []).slice().reverse();

  return (
    <Sheet open onOpenChange={next => !next && onClose()}>
      <SheetContent className="w-[480px] sm:w-[560px] sm:max-w-[560px] p-0 flex flex-col">
        <SheetHeader className="px-4 py-3 border-b">
          <SheetTitle className="flex items-center gap-2">
            {delivery ? eventTypeLabel(delivery.event_type) : 'Delivery'}
            {delivery && <DeliveryStatusBadge status={delivery.status} />}
          </SheetTitle>
          <SheetDescription className="font-mono text-xs">
            {delivery?.event_type ?? ''}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
          {query.isLoading && (
            <div className="space-y-3">
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          )}

          {query.isError && (
            <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                <p>{webhookErrorMessage(query.error, 'Could not load this delivery.')}</p>
              </div>
            </div>
          )}

          {delivery && (
            <>
              {retryAction && (
                <div className="flex flex-wrap gap-2">
                  {retryAction && permissions.canRetryDelivery && (
                    <SimpleTooltipV2
                      content={endpointActive ? '' : 'Resume the endpoint to retry its deliveries.'}
                    >
                      <Button
                        size="sm"
                        onClick={handleRetry}
                        disabled={retry.isPending || !endpointActive}
                        data-analytics-id={`webhooks:delivery:${retryAction}`}
                      >
                        {retry.isPending ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <RotateCw className="mr-2 h-4 w-4" />
                        )}
                        {deliveryRetryActionLabel(retryAction)}
                      </Button>
                    </SimpleTooltipV2>
                  )}
                </div>
              )}

              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <Field label="Event time">
                  <FormattedDateTime value={delivery.occurred_at} />
                </Field>
                <Field label="Endpoint">
                  {delivery.endpoint_name || delivery.url_host || EMPTY_VALUE}
                </Field>
                <Field label="Case ID">
                  <CopyableId value={delivery.case_id} label="Case ID" />
                </Field>
                <Field label="Reference ID">
                  <CopyableId value={delivery.reference_id} label="Reference ID" />
                </Field>
                <Field label="Event ID">
                  <CopyableId value={delivery.event_id} label="Event ID" />
                </Field>
                <Field label="Attempts">{delivery.attempt_count ?? 0}</Field>
                <Field label="Last status code">{delivery.last_status_code || EMPTY_VALUE}</Field>
                <Field label="Last error">{delivery.last_error_class || EMPTY_VALUE}</Field>
                <Field label="Next attempt">
                  {delivery.next_attempt_at ? (
                    <FormattedDateTime value={delivery.next_attempt_at} />
                  ) : (
                    EMPTY_VALUE
                  )}
                </Field>
                <Field label="Retries left">
                  {delivery.retries_left === undefined ? EMPTY_VALUE : delivery.retries_left}
                </Field>
                {delivery.succeeded_at && (
                  <Field label="Delivered">
                    <FormattedDateTime value={delivery.succeeded_at} />
                  </Field>
                )}
                {delivery.cancel_reason && (
                  <Field label="Cancelled because">{delivery.cancel_reason}</Field>
                )}
              </dl>

              <section className="space-y-2">
                <h3 className="text-sm font-semibold">Attempts</h3>
                <AttemptTimeline attempts={detail?.attempts ?? []} />
              </section>

              <section className="space-y-2">
                <h3 className="text-sm font-semibold">Event JSON</h3>
                <p className="text-xs text-muted-foreground">
                  The exact payload that was signed and sent. It carries ids only.
                </p>
                {detail?.event_json ? (
                  <JsonEditor
                    value={prettyEventJson(detail.event_json)}
                    readOnly
                    minHeight="0px"
                    maxHeight="16rem"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">{EMPTY_VALUE}</p>
                )}
              </section>

              <section className="space-y-2">
                <h3 className="text-sm font-semibold">Manual actions</h3>
                {actions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">None.</p>
                ) : (
                  <ul className="space-y-2">
                    {actions.map((action, index) => (
                      <li
                        key={`${action.at}-${index}`}
                        className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm"
                      >
                        <span>
                          <span className="font-medium">{manualActionLabel(action.kind)}</span>
                          <span className="text-muted-foreground">
                            {' '}
                            by {manualActionActor(action)}
                          </span>
                        </span>
                        <FormattedDateTime
                          value={action.at}
                          className="text-xs text-muted-foreground"
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
